import { z } from 'zod'

export const MemberSessionSchema = z.object({
  token: z.string(),
  member: z.object({
    id: z.string(),
    producerId: z.string(),
    name: z.string(),
    email: z.string(),
    hasPassword: z.boolean(),
  }),
  producerName: z.string(),
  expiresAt: z.string(),
})

export const MemberBrandSchema = z.object({
  producerName: z.string(),
  enabled: z.boolean(),
})

export const MemberMeSchema = z.object({
  id: z.string(),
  producerId: z.string(),
  name: z.string(),
  email: z.string(),
  hasPassword: z.boolean(),
  producerName: z.string(),
  avatarUrl: z.string().nullable(),
})

export const MemberLibraryItemSchema = z.object({
  productId: z.string(),
  name: z.string(),
  coverImage: z.string(),
  kind: z.enum(['course', 'files']),
  accessStatus: z.enum(['ACTIVE', 'PAST_DUE', 'LOCKED']),
  supportEmail: z.string(),
  updatePaymentUrl: z.string().nullable(),
  offerUrl: z.string().nullable().optional(),
  progressPercent: z.number(),
  continueLessonId: z.string().nullable(),
  continueLessonTitle: z.string().nullable(),
  fileCount: z.number(),
  totalSizeBytes: z.number(),
})

export const MemberLibrarySchema = z.object({
  gate: z.enum(['ok', 'EXPIRED', 'REVOKED']),
  producerName: z.string(),
  items: z.array(MemberLibraryItemSchema),
})

export const MemberLessonSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  kind: z.enum(['text', 'pdf', 'video']),
  locked: z.boolean(),
  availableAt: z.string().nullable(),
  completed: z.boolean(),
  positionSeconds: z.number(),
})

export const MemberCourseSchema = z.object({
  product: MemberLibraryItemSchema,
  producerName: z.string(),
  logoUrl: z.string().nullable(),
  modules: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      lessons: z.array(MemberLessonSummarySchema),
    }),
  ),
})

export const MemberLessonSchema = z.object({
  course: MemberCourseSchema,
  lesson: MemberLessonSummarySchema.extend({
    body: z.string(),
    playbackUrl: z.string().nullable(),
    assets: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        sizeBytes: z.number(),
        downloadUrl: z.string(),
      }),
    ),
  }),
  previousLessonId: z.string().nullable(),
  nextLessonId: z.string().nullable(),
  completedCourse: z.boolean(),
})

export const MemberFilesSchema = z.object({
  product: MemberLibraryItemSchema,
  producerName: z.string(),
  logoUrl: z.string().nullable(),
  files: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      sizeBytes: z.number(),
      downloadUrl: z.string(),
    }),
  ),
})

export const MemberCatalogOfferSchema = z.object({
  productId: z.string(),
  name: z.string(),
  coverImage: z.string(),
  description: z.string(),
  kind: z.enum(['course', 'files']),
  owned: z.boolean(),
  modules: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      lessonCount: z.number(),
    }),
  ),
  priceLabel: z.string().nullable(),
  checkoutUrl: z.string().nullable(),
})

export const MemberThankYouSchema = z.object({
  status: z.enum(['ready', 'pending']),
  accessUrl: z.string().optional(),
})

export type MemberSession = z.infer<typeof MemberSessionSchema>
export type MemberBrand = z.infer<typeof MemberBrandSchema>
export type MemberMe = z.infer<typeof MemberMeSchema>
export type MemberLibrary = z.infer<typeof MemberLibrarySchema>
export type MemberLibraryItem = z.infer<typeof MemberLibraryItemSchema>
export type MemberCourse = z.infer<typeof MemberCourseSchema>
export type MemberLesson = z.infer<typeof MemberLessonSchema>
export type MemberCatalogOffer = z.infer<typeof MemberCatalogOfferSchema>
export type MemberFiles = z.infer<typeof MemberFilesSchema>
